import { useAuth } from "@clerk/clerk-react";
import { useMemo } from "react";

const WORKER_URL = process.env.REACT_APP_WORKER_URL || "";

// ── Internal fetch helper ──────────────────────────────────────
async function apiFetch(path, options = {}, getToken) {
  const token = getToken ? await getToken() : null;

  const headers = {
    "Content-Type": "application/json",
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...(options.headers || {}),
  };

  const res = await fetch(`${WORKER_URL}${path}`, {
    ...options,
    headers,
  });

  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: res.statusText }));
    throw new Error(err.error || `API error ${res.status}`);
  }

  // 204 No Content
  if (res.status === 204) return null;

  return res.json();
}

// ── Factory: รับ getToken จาก Clerk ───────────────────────────
// getToken = () => clerk.session.getToken()
// ใช้งาน:
//   import { useAuth } from '@clerk/clerk-react';
//   const { getToken } = useAuth();
//   const client = createApiClient(getToken);
//   client.activities.list()
export function createApiClient(getToken) {
  return {
    // ── Profile ──────────────────────────────────────────────
    profile: {
      get: () =>
        apiFetch("/api/profile", { method: "GET" }, getToken),

      upsert: (data) =>
        apiFetch("/api/profile", {
          method: "PUT",
          body: JSON.stringify(data),
        }, getToken),
    },

    // ── Activities ───────────────────────────────────────────
    activities: {
      list: ({ from, to, limit } = {}) => {
        const params = new URLSearchParams();
        if (from) params.set("from", from);
        if (to) params.set("to", to);
        if (limit) params.set("limit", String(limit));
        const qs = params.toString() ? `?${params}` : "";
        return apiFetch(`/api/activities${qs}`, { method: "GET" }, getToken);
      },

      create: (data) =>
        apiFetch("/api/activities", {
          method: "POST",
          body: JSON.stringify(data),
        }, getToken),

      update: (id, data) =>
        apiFetch(`/api/activities/${id}`, {
          method: "PUT",
          body: JSON.stringify(data),
        }, getToken),

      delete: (id) =>
        apiFetch(`/api/activities/${id}`, { method: "DELETE" }, getToken),
    },

    // ── Plan Catalog ─────────────────────────────────────────
    planCatalog: {
      list: () =>
        apiFetch("/api/plan-catalog", { method: "GET" }, getToken),
    },

    // ── Feedback ─────────────────────────────────────────────
    feedback: {
      submit: (data) =>
        apiFetch("/api/feedback", {
          method: "POST",
          body: JSON.stringify({ ...data, user_agent: navigator.userAgent }),
        }, getToken),
    },

    // ── Bug Reports ──────────────────────────────────────────
    bugReports: {
      submit: (data) =>
        apiFetch("/api/bug-reports", {
          method: "POST",
          body: JSON.stringify({ ...data, user_agent: navigator.userAgent }),
        }, getToken),
    },

    // ── Admin ────────────────────────────────────────────────
    admin: {
      getFeedback: () =>
        apiFetch("/api/admin/feedback", { method: "GET" }, getToken),
    },
  };
}

export function useApi() {
  const { getToken } = useAuth();
  return useMemo(() => createApiClient(getToken), [getToken]);
}
